/**
 * Coupon studio: physical sheet geometry, dependency-free binary PDF + ZIP.
 * Canvas rendering lives in couponCanvas.mjs; the PDF uses exactly the same JPEG.
 * Files are generated locally in the browser (coupon codes never enter URLs).
 */
const enc = new TextEncoder();
const utf8 = (text) => enc.encode(String(text));
const n = (v) => Math.round(v * 1000) / 1000;

export const defaultDesign = () => ({
  version: 1,
  page: {paper:'letter',orientation:'portrait',columns:2,rows:4,marginX:7,marginY:7,gapX:3,gapY:3,cutGuides:true},
  canvas: {background:'#ffffff',terms:'Válido una vez. Aplican restricciones.',elements:[
    {id:'title',type:'text',text:'{{discount}} EN TU PRÓXIMA COMPRA',x:.08,y:.07,w:.84,h:.17,fontSize:47,color:'#142c3a',bold:true,align:'center',image:''},
    {id:'sub',type:'text',text:'{{campaign}}',x:.08,y:.24,w:.54,h:.14,fontSize:29,color:'#346273',bold:false,align:'left',image:''},
    {id:'qr',type:'qr',text:'',x:.72,y:.24,w:.22,h:.40,fontSize:16,color:'#000000',bold:false,align:'center',image:''},
    {id:'code',type:'text',text:'{{code}}',x:.08,y:.59,w:.60,h:.13,fontSize:27,color:'#233c4b',bold:true,align:'left',image:''},
    {id:'small',type:'text',text:'Válido hasta: {{valid_until}}',x:.08,y:.73,w:.84,h:.10,fontSize:18,color:'#60717b',bold:false,align:'left',image:''},
  ]}
});

export function sheetGeometry(page) {
  const paper = page.paper === 'a4' ? {w:210,h:297} : {w:215.9,h:279.4};
  const horizontal = page.orientation === 'landscape';
  const w = horizontal ? paper.h : paper.w;
  const h = horizontal ? paper.w : paper.h;
  const cols = Number(page.columns), rows = Number(page.rows);
  const mx = Number(page.marginX), my = Number(page.marginY);
  const gx = Number(page.gapX), gy = Number(page.gapY);
  if (![cols,rows,mx,my,gx,gy].every(Number.isFinite) || !Number.isInteger(cols) || !Number.isInteger(rows) ||
      cols < 1 || cols > 6 || rows < 1 || rows > 8 || cols*rows > 24 ||
      mx < 0 || my < 0 || gx < 0 || gy < 0 || mx > 35 || my > 35 || gx > 35 || gy > 35) {
    throw new Error('La distribución de la hoja no es válida.');
  }
  const cw = (w-2*mx-(cols-1)*gx)/cols;
  const ch = (h-2*my-(rows-1)*gy)/rows;
  if(cw < 32 || ch < 24) throw new Error('Los cupones no caben en la hoja. Reduce márgenes, separación o cantidad.');
  const slots = [];
  for (let row=0; row<rows; row++) for(let col=0; col<cols; col++) {
    slots.push({xMm:n(mx+col*(cw+gx)),yMm:n(my+row*(ch+gy)),wMm:n(cw),hMm:n(ch)});
  }
  return {widthMm:w,heightMm:h,couponWidthMm:cw,couponHeightMm:ch,slots,perPage:cols*rows};
}

export function resolveFields(text, campaign={}, coupon={}, number=1) {
  const value = campaign.discount_type === 'percent'
    ? `${Number(campaign.discount_value||0).toLocaleString('es-MX')}%`
    : `$${Number(campaign.discount_value||0).toLocaleString('es-MX',{minimumFractionDigits:2})}`;
  const valid = campaign.ends_on ? String(campaign.ends_on).slice(0,10) : 'Según condiciones';
  const replacements = {
    campaign:String(campaign.name||'Nombre de campaña'),
    discount:value,
    code:String(coupon.code||'GT-CODIGO-DE-EJEMPLO'),
    valid_until:valid,
    number:String(number),
    terms:String(campaign.terms||'Aplican restricciones de la campaña.'),
  };
  return String(text||'').replace(/\{\{\s*(campaign|discount|code|valid_until|number|terms)\s*\}\}/g, (_,k)=>replacements[k]);
}

function writer() {
  const parts=[]; let length=0;
  return {write(chunk){const bytes=typeof chunk==='string'?utf8(chunk):chunk;parts.push(bytes);length+=bytes.length;},
    get length(){return length;}, blob(mime){return new Blob(parts,{type:mime});}};
}

/**
 * PDF pages array: {widthMm,heightMm,images:[{data,widthPx,heightPx,xMm,yMm,wMm,hMm}],guides}.
 * JPEG RGB pixel data is embedded natively (DCTDecode), no rasterized whole sheets.
 */
export function makePdf(pages) {
  if(!Array.isArray(pages) || pages.length < 1 || pages.length > 5000) throw new Error('Número de páginas inválido.');
  const objs=[]; const reserve=()=>{objs.push(null);return objs.length;};
  const catalog=reserve(), pagesIndex=reserve();
  const pageIds=[];
  for(const page of pages) {
    const pageId=reserve(), contentId=reserve();pageIds.push(pageId);
    const pixels=page.images||[];
    const resources=[];const commands=[];
    const W=page.widthMm*72/25.4,H=page.heightMm*72/25.4;
    for(let i=0;i<pixels.length;i++) {
      const img=pixels[i];
      if(!(img.data instanceof Uint8Array) || img.data.length < 4 || img.data[0] !== 0xff || img.data[1] !== 0xd8)
        throw new Error('No se recibió un JPEG válido.');
      const imgId=reserve();
      const key='Im'+i;resources.push(`/${key} ${imgId} 0 R`);
      objs[imgId-1]={prefix:`<< /Type /XObject /Subtype /Image /Width ${img.widthPx} /Height ${img.heightPx} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${img.data.length} >>\nstream\n`,data:img.data};
      const x=img.xMm*72/25.4,y=H-(img.yMm+img.hMm)*72/25.4,w=img.wMm*72/25.4,h=img.hMm*72/25.4;
      commands.push(`q ${n(w)} 0 0 ${n(h)} ${n(x)} ${n(y)} cm /${key} Do Q`);
      if(page.guides) commands.push(`q 0.48 w 0.7 G ${n(x)} ${n(y)} ${n(w)} ${n(h)} re S Q`);
    }
    const content=utf8(commands.join('\n')+'\n');
    objs[contentId-1]={prefix:`<< /Length ${content.length} >>\nstream\n`,data:content};
    objs[pageId-1]=`<< /Type /Page /Parent ${pagesIndex} 0 R /MediaBox [0 0 ${n(W)} ${n(H)}] /Resources << /XObject << ${resources.join(' ')} >> >> /Contents ${contentId} 0 R >>`;
  }
  objs[catalog-1]=`<< /Type /Catalog /Pages ${pagesIndex} 0 R >>`;
  objs[pagesIndex-1]=`<< /Type /Pages /Count ${pages.length} /Kids [${pageIds.map(id=>`${id} 0 R`).join(' ')}] >>`;
  const out=writer();out.write(new Uint8Array([37,80,68,70,45,49,46,52,10,37,226,227,207,211,10]));
  const offsets=[0];
  for(let i=0;i<objs.length;i++) {
    offsets.push(out.length);
    out.write(`${i+1} 0 obj\n`);
    const item=objs[i];
    if(typeof item==='string')out.write(item+'\n');
    else if(item && item.data){out.write(item.prefix);out.write(item.data);out.write('\nendstream\n');}
    else throw new Error('Objeto PDF incompleto.');
    out.write('endobj\n');
  }
  const xref=out.length;
  out.write(`xref\n0 ${objs.length+1}\n0000000000 65535 f \n`);
  for(const offset of offsets.slice(1))out.write(`${String(offset).padStart(10,'0')} 00000 n \n`);
  out.write(`trailer\n<< /Size ${objs.length+1} /Root ${catalog} 0 R >>\nstartxref\n${xref}\n%%EOF\n`);
  return out.blob('application/pdf');
}

// ZIP store method is intentionally dependency-free and compatible with Windows Explorer.
const crcTable=Array.from({length:256},(_,i)=>{let c=i;for(let j=0;j<8;j++)c=(c&1)?0xedb88320^(c>>>1):c>>>1;return c>>>0;});
export function crc32(bytes){let crc=0xffffffff;for(const b of bytes)crc=crcTable[(crc^b)&255]^(crc>>>8);return (crc^0xffffffff)>>>0;}
const le=(size, values)=>{const buf=new Uint8Array(size);const v=new DataView(buf.buffer);for(const [pos,num,bits] of values)bits===16?v.setUint16(pos,num,true):v.setUint32(pos,num,true);return buf;};
export function makeZip(files) {
  if(!Array.isArray(files)||files.length>10000) throw new Error('Demasiados archivos ZIP.');
  const chunks=[],central=[];let off=0;
  for(const file of files){
    const name=String(file.name||'archivo').replace(/[\\/:*?"<>|\x00-\x1f]/g,'_').slice(0,180);
    const filename=utf8(name),data=file.data;
    if(!(data instanceof Uint8Array)||data.length>0xffffffff||off>0xffffffff)throw new Error('Archivo demasiado grande para ZIP estándar.');
    const crc=crc32(data);
    const h=le(30,[[0,0x04034b50,32],[4,20,16],[6,0x800,16],[8,0,16],[14,crc,32],[18,data.length,32],[22,data.length,32],[26,filename.length,16]]);
    chunks.push(h,filename,data);
    const c=le(46,[[0,0x02014b50,32],[4,20,16],[6,20,16],[8,0x800,16],[10,0,16],[16,crc,32],[20,data.length,32],[24,data.length,32],[28,filename.length,16],[42,off,32]]);
    central.push(c,filename);off+=h.length+filename.length+data.length;
  }
  const centralSize=central.reduce((sum,v)=>sum+v.length,0);
  if(off+centralSize>0xffffffff)throw new Error('ZIP supera el límite de 4 GB.');
  const eocd=le(22,[[0,0x06054b50,32],[8,files.length,16],[10,files.length,16],[12,centralSize,32],[16,off,32]]);
  return new Blob([...chunks,...central,eocd],{type:'application/zip'});
}

export async function blobBytes(blob){return new Uint8Array(await blob.arrayBuffer());}
export function dataUrlBytes(dataUrl){const base64=dataUrl.slice(dataUrl.indexOf(',')+1);const str=atob(base64);const bytes=new Uint8Array(str.length);for(let i=0;i<str.length;i++)bytes[i]=str.charCodeAt(i);return bytes;}
export function downloadBlob(blob,filename){const url=URL.createObjectURL(blob);const a=document.createElement('a');a.href=url;a.download=filename;document.body.appendChild(a);a.click();a.remove();setTimeout(()=>URL.revokeObjectURL(url),30000);}
export function makeCsv(campaign,codes){const escape=(value)=>{let str=String(value??'');if(/^[=+@\-\t\r]/.test(str))str="'"+str;return '"'+str.replace(/"/g,'""')+'"';};return '\ufeff'+[['numero','campania','codigo','estado','usos','lote'],...codes.map((c,i)=>[i+1,campaign.name,c.code,c.status,c.uses_count+'/'+c.max_uses,c.batch_id])].map(r=>r.map(escape).join(',')).join('\r\n');}
